import { useNavigate } from '@tanstack/react-router';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { AlertContext } from '../../../../contexts/alert-context';
import { OrderStatus } from '../../../../data/order';
import type { OrderItem } from '../../../../data/order-item';
import type { OrderOptions } from '../../../../data/order-options';
import type { Product } from '../../../../data/product';
import {
  useDeleteOrder,
  useGetOrder,
  useSaveOrderItem,
  useSaveOrderOptions,
} from '../../../../hooks/orders';
import { useProducts } from '../../../../hooks/products';
import { useActive } from '../../../../hooks/use-active';
import {
  getRemainingDirtyItems,
  initOrderItems,
  itemHasUserCount,
  mergeDirtyItems,
  mergeOrderItems,
  type ProductOrderItem,
  replaceItem,
  simplifyOrderItem,
  updateOrderQuanties,
} from '../data/product-order-item';
import type { EditOrderPopulatedProperties } from '../types';
import { newUserFamilyId, userFamilyIdForBody } from '../utils/family-id';

const REFRESH_DELAY_MS = 15 * 1000; // fifteen seconds

// Every item write serializes on the order's single `version` attribute
// server-side, and the API retries a conflict at most 10 times — so in a burst
// of N simultaneous saves the writers past the tenth exhaust their budget and
// surface a 500, no matter how long they back off. Allocating a tier (or a
// days-supply target) across a large product family dirties every SKU in it at
// once, which on 2026-07-19 sent 23-34 concurrent saves at a single order and
// failed 38 of them. Saving in small sequential batches keeps contention inside
// the server's retry budget. The real fix is a single order-level bulk save.
const SAVE_BATCH_SIZE = 5;

export function useOrderItems(properties: EditOrderPopulatedProperties) {
  const [order, setOrder] = useState(properties.order);
  const [options, setOptions] = useState<OrderOptions>(order.options);
  const [savingOptions, setSavingOptions] = useState(false);
  const products = properties.products;
  const store = properties.store;
  const active = useActive();

  const [dirtyItems, setDirtyItems] = useState<ProductOrderItem[]>([]);
  const saveOrderItem = useSaveOrderItem();
  const saveOrderOptions = useSaveOrderOptions();
  const { updateProduct, saving: productSaving } = useProducts();
  const { addErrorAlert } = useContext(AlertContext);

  // Start calculating so the first frame shows the spinner until the initial
  // seed completes (below), without flashing an empty table.
  const [calculating, setCalculating] = useState(true);
  const [initialized, setInitialized] = useState(false);
  const navigate = useNavigate();
  const [orderItems, setOrderItems] = useState<ProductOrderItem[]>([]);

  const getOrder = useGetOrder();
  const deleteOrder = useDeleteOrder();

  const createOrderItems = useCallback(() => {
    // delay the calculation so calculating is set and displayed before this blocks UI update
    setTimeout(() => {
      const updatedOrderItems = initOrderItems(order.items, products, options);
      setOrderItems(updatedOrderItems);
      setCalculating(false);
    }, 300);
  }, [order, options, products]);

  // effect to seed order items on first load
  // not bound to order, as we don't want this recalculated for order refresh.
  // Gated on an explicit `initialized` flag rather than `orderItems.length` —
  // an order that genuinely resolves to zero items (e.g. a draft whose vendor
  // has no products) must seed exactly once, not re-fire forever and hang the
  // page on an infinite spinner.
  useEffect(() => {
    if (initialized) {
      return;
    }

    setInitialized(true);
    createOrderItems();
  }, [initialized, createOrderItems]);

  // React Query cache patches (e.g. setting Min QOH via Actions > Edit) hand
  // us a new products array. Re-sync orderItems' Product references so the
  // reorder label re-evaluates and the table re-sorts immediately — without
  // this the row would stay on its mount-time snapshot until the next 15s
  // refresh. Options changes go through `setOptionsCallback` →
  // `debouncedCreateOrderItems`, so don't fire here on every reorder-settings
  // keystroke.
  // biome-ignore lint/correctness/useExhaustiveDependencies: options change is handled by the debounced rebuild path above
  useEffect(() => {
    setOrderItems((items) => updateOrderQuanties(items, products, options));
  }, [products]);

  const debouncedCreateOrderItems = useDebouncedCallback(createOrderItems, 500);

  const saveOrderOptionsCallback = useCallback(() => {
    return saveOrderOptions(store.id, order.id, options).then(() => {
      setOrder((order) => ({ ...order, options }));
      setSavingOptions(false);
    });
  }, [order, options, store, saveOrderOptions]);

  const debouncedSaveOrderOptions = useDebouncedCallback(saveOrderOptionsCallback, 3000);

  // explicitly updating order when options change. If we try to do this with a useEffect,
  // then updating the order will trigger the useEffect again, causing an infinite loop
  const setOptionsCallback = useCallback(
    (options: OrderOptions) => {
      setSavingOptions(true); // prevent refreshOrder from firing while we save options
      setOptions(options);

      debouncedCreateOrderItems();
      debouncedSaveOrderOptions();
    },
    [debouncedCreateOrderItems, debouncedSaveOrderOptions]
  );

  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  const promptDeleteOrder = useCallback(() => {
    if (order) {
      setShowCancelConfirm(true);
    }
  }, [order]);

  const dismissCancelConfirm = useCallback(() => {
    setShowCancelConfirm(false);
  }, []);

  const confirmDeleteOrder = useCallback(() => {
    if (!order) {
      return;
    }

    setShowCancelConfirm(false);
    deleteOrder(order).then((success) => {
      if (success) {
        navigate({
          to: '/stores/$storeId/orders',
          params: { storeId: order.store_id },
        });
      }
    });
  }, [deleteOrder, navigate, order]);

  const saveDirtyItems = useCallback(() => {
    if (!order) {
      return;
    }

    const execute = async () => {
      const updatedItems: OrderItem[] = [];
      for (let i = 0; i < dirtyItems.length; i += SAVE_BATCH_SIZE) {
        const batch = await Promise.all(
          dirtyItems.slice(i, i + SAVE_BATCH_SIZE).map((dirtyItem: ProductOrderItem) => {
            return saveOrderItem(store.id, order, simplifyOrderItem(dirtyItem));
          })
        );
        updatedItems.push(...batch);
      }

      // 1. get current list of dirtyItems (they may have changed since the above started)
      // 2. filter out any items that match the server response, those changes have been persisted
      setDirtyItems((dirtyItems) => getRemainingDirtyItems({ dirtyItems, updatedItems }));
    };

    execute();
  }, [store, order, dirtyItems, saveOrderItem]);

  const debouncedSaveDirtyItems = useDebouncedCallback(saveDirtyItems, 1000);

  const setOrderItem = useCallback(
    (orderItem: ProductOrderItem) => {
      setOrderItems((items) => replaceItem(items, orderItem));
      setDirtyItems((dirtyItems) => mergeDirtyItems(dirtyItems, orderItem));
      debouncedSaveDirtyItems();
    },
    [debouncedSaveDirtyItems]
  );

  // family_id lives on Product, not OrderItem, so it's persisted via
  // updateProduct (React Query cache patch) — no order-item dirty save.
  const patchProductFamilyId = useCallback(
    async (product: Product, nextFamilyId: string) => {
      await updateProduct({ ...product, family_id: nextFamilyId });
      setOrderItems((items) =>
        items.map((i) =>
          i.product.id === product.id
            ? { ...i, product: { ...i.product, family_id: nextFamilyId } }
            : i
        )
      );
    },
    [updateProduct]
  );

  const joinProductToFamily = useCallback(
    (product: Product, familyBody: string) =>
      patchProductFamilyId(product, userFamilyIdForBody(familyBody)),
    [patchProductFamilyId]
  );

  const removeProductFromFamily = useCallback(
    (product: Product) => patchProductFamilyId(product, newUserFamilyId()),
    [patchProductFamilyId]
  );

  const resetOrderCallback = useCallback(() => {
    if (!order) {
      return;
    }

    const userItems = orderItems.filter(itemHasUserCount);
    if (userItems.length === 0) {
      return;
    }

    const response = confirm(
      `This will reset changes you have made to ${userItems.length} item(s) this order. Are you sure you want to continue?`
    );
    if (!response) {
      return;
    }

    for (const orderItem of userItems) {
      setOrderItem({
        ...orderItem,
        cases: undefined,
        units: undefined,
      });
    }
  }, [setOrderItem, order, orderItems]);

  const [lastRefreshed, setLastRefreshed] = useState(new Date());

  const refreshOrder = useCallback(() => {
    // don't refresh this cycle if we have pending saves
    if (dirtyItems.length > 0) {
      return;
    }

    // don't refresh if we're saving options, this prevents a data race
    if (savingOptions) {
      return;
    }

    // don't refresh if the data is already fresh, this helps prevent flickering when
    // initially loading the order
    if (lastRefreshed.getTime() > Date.now() - 1000) {
      return;
    }

    getOrder(store.id, order.id).then((order) => {
      if (order.status !== OrderStatus.Draft) {
        navigate({
          to: '/stores/$storeId/orders/$orderId/summary',
          params: { storeId: order.store_id, orderId: order.id },
        });
        addErrorAlert('Order has been submitted and can no longer be edited.', {
          keepAfterRouteChange: true,
        });
      } else {
        // simply merge in the server-side order items with the client-side order items
        setLastRefreshed(new Date());
        setOrder(order);
        setOptions(order.options);
        setOrderItems((existingItems) => {
          // merge in any inbound changes
          const mergedItems = mergeOrderItems({
            existingItems,
            incomingItems: order.items,
          });

          // recalculate quantities to apply any updated options
          return updateOrderQuanties(mergedItems, products, order.options);
        });
      }
    });
  }, [
    lastRefreshed,
    dirtyItems,
    savingOptions,
    addErrorAlert,
    navigate,
    getOrder,
    order.id,
    store.id,
    products,
  ]);

  // dirtyItems changing causes refreshOrder to change, which then caused the active useEffect to fire.
  // Storing the the callback inside a ref allows the useEffect to fire only for active changes.
  const refreshOrderRef = useRef(refreshOrder);

  useEffect(() => {
    refreshOrderRef.current = refreshOrder;
  }, [refreshOrder]);

  useEffect(() => {
    // When the user becomes active again (tab visible + recent input), refresh the order immediately
    if (active) {
      refreshOrderRef.current();
    }

    const timer = setInterval(() => {
      // note: this doesn't work for Safari as of 29-06-2024
      // https://developer.mozilla.org/en-US/docs/Web/API/Network_Information_API
      if (navigator.onLine && active) {
        refreshOrderRef.current();
      }
    }, REFRESH_DELAY_MS);

    return () => clearInterval(timer);
  }, [active]);

  return {
    order,
    orderItems,
    setOrderItem,
    joinProductToFamily,
    removeProductFromFamily,
    productSaving,
    calculating,
    resetOrder: resetOrderCallback,
    showCancelConfirm,
    promptDeleteOrder,
    dismissCancelConfirm,
    confirmDeleteOrder,
    options,
    setOptions: setOptionsCallback,
    lastRefreshed,
    paused: !active,
  };
}
